import { useEffect, useState } from "react";
import { Globe2, Building2, Plane, MapPinned, Plus, Trash2, ExternalLink, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import MapView from "../MapView";

const KINDS = [
  { key: "country", label: "Countries", icon: Globe2, color: "text-blue-600" },
  { key: "city", label: "Cities", icon: MapPinned, color: "text-violet-600" },
  { key: "airport", label: "Airports", icon: Building2, color: "text-emerald-600" },
  { key: "airline", label: "Airlines", icon: Plane, color: "text-rose-600" },
];

export default function TravelStats() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "airport", ref: "", label: "", date: "", notes: "", link: "" });

  const load = () => api.get("/trips/stats/summary").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.label.trim() && !form.ref.trim()) { toast.error("Enter a place/name"); return; }
    try {
      await api.post("/trips/visits", { ...form, links: form.link ? [form.link] : [] });
      setForm({ kind: form.kind, ref: "", label: "", date: "", notes: "", link: "" }); setOpen(false); load(); toast.success("Added");
    } catch { toast.error("Could not add"); }
  };
  const del = async (id) => { try { await api.delete(`/trips/visits/${id}`); load(); } catch {} };

  if (!data) return null;
  const points = data.map_points || [];
  const trips = data.trips || {};
  const airportPts = (trips.airports || []).map((a) => ({ lat: a.lat, lon: a.lon, radius: 5, color: "#059669", popup: `${a.iata} — ${a.name}` }));
  const allPts = [...points.map((p) => ({ lat: p.lat, lon: p.lon, radius: p.kind === "country" ? 7 : 5,
    color: { country: "#EA4335", city: "#0047FF", airport: "#F59E0B", airline: "#7C3AED" }[p.kind] || "#0047FF", popup: p.label })), ...airportPts];
  const lines = (trips.lines || []).map((l) => ({ from: l.from, to: l.to, style: { color: "#059669", weight: 1.5, opacity: 0.6 } }));

  return (
    <div data-testid="travel-stats">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-[#EA4335] text-2xl flex items-center gap-2"><Trophy className="w-6 h-6" /> My Travel Map & Stats</h2>
        <button onClick={() => setOpen((o) => !o)} data-testid="visit-new" className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Log a visit</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300 mb-3">
        {KINDS.map((k) => (
          <div key={k.key} className="bg-white p-4 text-center" data-testid={`stat-${k.key}`}>
            <k.icon className={`w-5 h-5 mx-auto mb-1 ${k.color}`} />
            <div className="font-heading font-900 text-3xl">{data.counts[k.key] || 0}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{k.label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-emerald-200 border border-emerald-300 mb-6" data-testid="stats-from-trips">
        <div className="bg-emerald-50/60 p-4 text-center" data-testid="stat-trip-countries">
          <Globe2 className="w-5 h-5 mx-auto mb-1 text-emerald-700" />
          <div className="font-heading font-900 text-3xl" title={(trips.countries || []).join(", ")}>{trips.countries_count || 0}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Countries (from trips)</div>
        </div>
        <div className="bg-emerald-50/60 p-4 text-center" data-testid="stat-trip-airports">
          <Building2 className="w-5 h-5 mx-auto mb-1 text-emerald-700" />
          <div className="font-heading font-900 text-3xl">{trips.airports_count || 0}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Trip airports</div>
        </div>
        <div className="bg-emerald-50/60 p-4 text-center" data-testid="stat-trip-flights">
          <Plane className="w-5 h-5 mx-auto mb-1 text-emerald-700 -rotate-45" />
          <div className="font-heading font-900 text-3xl">{trips.flights || 0}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Flights logged</div>
        </div>
        <div className="bg-emerald-50/60 p-4 text-center" data-testid="stat-trip-miles">
          <Globe2 className="w-5 h-5 mx-auto mb-1 text-emerald-700" />
          <div className="font-heading font-900 text-3xl tabular-nums">{(trips.miles_flown || 0).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Miles flown (from trips)</div>
        </div>
      </div>

      {open && (
        <div className="border border-primary bg-blue-50/40 p-4 mb-6" data-testid="visit-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="border border-zinc-300 px-3 py-2 text-sm">
              {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label.slice(0, -1)}</option>)}
            </select>
            <input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="Code (e.g. DEL, UA) — optional" className="border border-zinc-300 px-3 py-2 text-sm" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Name (Delhi, India, United…)" data-testid="visit-label" className="border border-zinc-300 px-3 py-2 text-sm" />
            <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" className="border border-zinc-300 px-3 py-2 text-sm" />
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / comments" className="sm:col-span-2 border border-zinc-300 px-3 py-2 text-sm" />
            <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Link (photo album, blog…) — optional" className="sm:col-span-2 border border-zinc-300 px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="visit-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">Add</button>
          </div>
        </div>
      )}

      {(allPts.length > 0 || lines.length > 0) && (
        <div className="mb-6">
          <MapView center={allPts[0] ? [allPts[0].lat, allPts[0].lon] : [20, 0]} zoom={2} height="360px"
            markers={allPts} lines={lines} />
          <div className="flex flex-wrap gap-4 mt-1.5 text-[11px] text-muted-foreground" data-testid="stats-map-legend">
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#EA4335] mr-1" />Countries</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#0047FF] mr-1" />Cities</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#F59E0B] mr-1" />Airports (logged)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#059669] mr-1" />Trip airports &amp; flight routes</span>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        {KINDS.map((k) => {
          const rows = data.by[k.key] || [];
          if (!rows.length) return null;
          return (
            <div key={k.key}>
              <h3 className={`font-heading font-900 text-lg flex items-center gap-2 ${k.color} mb-2`}><k.icon className="w-5 h-5" /> {k.label}</h3>
              <div className="border border-zinc-300 bg-white divide-y divide-zinc-100">
                {rows.map((v) => (
                  <div key={v.id} className="flex items-start justify-between px-3 py-2 group" data-testid={`visit-${v.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{v.label} {v.date && <span className="text-xs font-mono text-muted-foreground">· {v.date}</span>}</div>
                      {v.notes && <div className="text-xs text-zinc-600">{v.notes}</div>}
                      {v.links?.map((l, i) => <a key={i} href={l} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline"><ExternalLink className="w-3 h-3" />{l.slice(0, 40)}</a>)}
                    </div>
                    <button onClick={() => del(v.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
